import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

const STAGES = [
  { key: "en_attente", label: "En attente" },
  { key: "contacte", label: "Contacté" },
  { key: "rdv_fixe", label: "RDV fixé" },
  { key: "devis_envoye", label: "Devis envoyé" },
  { key: "en_negociation", label: "En négociation" },
];

export function ProjectCreateModal({ open, onClose, contactId, contactName, onCreated }) {
  const [sources, setSources] = useState([]);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState({
    title: "",
    source_id: "",
    stage: "en_attente",
    assigned_to_user_id: "none",
    project_description: "",
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    Promise.all([
      api.get("/lead-sources").catch(() => ({ data: [] })),
      api.get("/team/members").catch(() => ({ data: [] })),
    ])
      .then(([s, m]) => {
        if (!alive) return;
        const activeSources = (s.data || []).filter((x) => x.active);
        setSources(activeSources);
        // BLOC 19/10 — Filtrage is_active : désactivés exclus du dropdown d'assignation
        setMembers((m.data || []).filter((u) => u.is_active !== false));
        if (activeSources.length && !form.source_id) {
          setForm((f) => ({ ...f, source_id: activeSources[0].id }));
        }
      });
    return () => { alive = false; };
     
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.source_id) {
      setError("Sélectionne une source de lead.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      const payload = {
        contact_id: contactId,
        source_id: form.source_id,
        title: form.title.trim() || `Projet ${contactName || ""}`.trim(),
        stage: form.stage,
        project_description: form.project_description.trim(),
        assigned_to_user_id: form.assigned_to_user_id === "none" ? null : form.assigned_to_user_id,
        tags: [],
      };
      const { data } = await api.post("/projects", payload);
      toast.success("Projet créé");
      onCreated?.(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && !saving && onClose?.()}>
      <DialogContent
        className="max-w-lg border-slate-800 bg-[#1E1A17] text-slate-100"
        data-testid="project-create-modal"
      >
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="text-slate-100">Nouveau projet</DialogTitle>
            <DialogDescription className="text-slate-400">
              Sur le contact <span className="text-slate-200">{contactName || "sans nom"}</span>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3 py-3">
            <div>
              <Label htmlFor="pc-title" className="text-xs text-slate-400">Titre du projet</Label>
              <Input
                id="pc-title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder={`Projet ${contactName || ""}`.trim()}
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="pc-title"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Source</Label>
                <Select
                  key={`src-${form.source_id || "empty"}`}
                  value={form.source_id}
                  onValueChange={(v) => setForm({ ...form, source_id: v })}
                >
                  <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="pc-source">
                    <SelectValue placeholder="Sélectionner…" />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    {sources.map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Statut initial</Label>
                <Select value={form.stage} onValueChange={(v) => setForm({ ...form, stage: v })}>
                  <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="pc-stage">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                    {STAGES.map((s) => (
                      <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Commercial du projet</Label>
              <Select value={form.assigned_to_user_id} onValueChange={(v) => setForm({ ...form, assigned_to_user_id: v })}>
                <SelectTrigger className="border-slate-700 bg-slate-950/50" data-testid="pc-assignee">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="border-slate-700 bg-slate-900 text-slate-100">
                  <SelectItem value="none">Non assigné</SelectItem>
                  {members.map((m) => (
                    <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="pc-desc" className="text-xs text-slate-400">Description</Label>
              <Textarea
                id="pc-desc"
                value={form.project_description}
                onChange={(e) => setForm({ ...form, project_description: e.target.value })}
                rows={3}
                placeholder="Ex : Installation photovoltaïque 12 panneaux…"
                className="border-slate-700 bg-slate-950/50 text-slate-100"
                data-testid="pc-desc"
              />
            </div>
            {error && (
              <div className="rounded border border-red-800/50 bg-red-900/20 p-2 text-xs text-red-200" data-testid="pc-error">
                {error}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={saving}
              className="border-slate-700 bg-slate-800/50 text-slate-200"
              data-testid="pc-cancel"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.source_id}
              className="bg-[#C4553B] text-white hover:bg-[#B44A32]"
              data-testid="pc-submit"
            >
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Créer le projet
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default ProjectCreateModal;
